/*
 * Kế hoạch thanh toán (tên cũ Cọc tại cơ sở) — theo phiếu báo giá chính thức: tổng chi phí, đã thu, còn lại; số lần thanh toán;
 * đợt 1 nhập số tiền khách trả hôm nay; các đợt sau không bắt hẹn ngày (họp 30/10: hỏi khách khi nào trả rất tế nhị).
 * Quầy POS thu và chọn hình thức thanh toán; Tư vấn không cầm tiền.
 */
import React, { useEffect, useState } from 'react';
import { Button, DatePicker, InputNumber, Popconfirm, Select, Tag, Tooltip, message } from 'antd';
import { Lock, Pencil, Trash2 } from 'lucide-react';
import type { ColumnType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  DEP_METHODS, PAY_STEP_ST, PLAN_MAX, SITE_DEP_ST, allCodes, buildPayPlan, calc, nextCode,
  paidOf, payRows, payStepText, planIssues, quoteValid, renumber, suggestDeposit, vnd,
  type PayRow, type PayStep, type SiteDeposit, type Visit,
} from './logic';
import { useTuVan } from './store';
import { Card, DoctorGate, FlowBar, Hint, NoVisit, NotStarted, RTable, Row, Shell, useMe, useNow, useVisit } from './shared';

interface DepRow { key: string; code: string; where: string; purpose: string; amount: number; status: React.ReactNode; at?: number; site?: SiteDeposit }

const dmy = (t?: number) => (t ? dayjs(t).format('DD/MM/YYYY') : 'Chưa hẹn ngày');
const money = (x: number | null | undefined) => `${x ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const unmoney = (x: string | undefined) => Number((x ?? '').replace(/\./g, ''));
const stepState = payStepText;

function Body({ v }: { v: Visit }) {
  const me = useMe();
  const now = useNow();
  const { visits, update } = useTuVan();
  const q = v.quote;
  const total = q ? calc(q).total : 0;
  const paid = paidOf(v);
  const pending = v.deposits.filter((d) => d.status === 'requested').reduce((s, d) => s + d.amount, 0);
  const room = Math.max(0, total - paid - pending);
  const ok = quoteValid(q) && (v.status === 'consulting' || v.status === 'done');
  const planned = !!v.payPlan?.length;
  const rowsPlan = payRows(v, now);
  // Thu lần lượt: chỉ mở đợt kế tiếp khi các đợt trước đã thu xong và đợt này chưa gửi quầy
  const firstOpen = rowsPlan.find((r) => r.state !== 'paid');
  const nextStep = firstOpen && !firstOpen.requested ? firstOpen : undefined;

  // Đợt 1 gợi ý ~20% (khách nói số khác thì nhập đè)
  const defFirst = Math.min(room, suggestDeposit(total));
  const [editing, setEditing] = useState(!planned);
  // Đã lập kế hoạch → đợt đã thu (kể cả thu một phần) / đang chờ quầy thu bị khoá; tổng giữ nguyên như kế hoạch cũ
  const locked = rowsPlan.filter((r) => r.state === 'paid' || r.requested || r.paid > 0).map((r) => r.no);
  const target = planned ? v.payPlan!.reduce((s, x) => s + x.amount, 0) : room;
  const [draft, setDraft] = useState<PayStep[]>(() => (planned ? v.payPlan! : buildPayPlan(room, 1, room, now)));
  // Chưa lập kế hoạch, đang trả 1 lần: số phải thu đổi (quầy xác nhận cọc online, phiếu được duyệt giảm) thì nhập theo.
  // Đã chia nhiều đợt thì giữ số tư vấn viên nhập, dùng "Dồn phần lệch vào đợt cuối"
  useEffect(() => {
    if (!planned) setDraft((xs) => (xs.length === 1 && xs[0].amount !== room ? buildPayPlan(room, 1, room, now, 0) : xs));
  }, [room, planned]); // eslint-disable-line react-hooks/exhaustive-deps
  const issues = planIssues(draft, target, now, planned ? locked : []);
  const draftSum = draft.reduce((s, x) => s + x.amount, 0);

  // Chọn số lần → chia phần còn lại đều cho các lần sau, chưa hẹn ngày (đợt đã khoá giữ nguyên); sửa số tiền / hẹn ngày nếu khách nói
  const pickCount = (count: number) => {
    const keep = planned ? draft.filter((s) => locked.includes(s.no)) : [];
    const rest = target - keep.reduce((s, x) => s + x.amount, 0);
    const n = Math.max(1, count - keep.length);
    // Đã chia nhiều lần và đã nhập lần 1 (số khách trả hôm nay) thì giữ đúng số đó, kể cả thấp hơn mức gợi ý
    const typed = draft.length > 1 && draft[0]?.amount > 0 ? draft[0].amount : undefined;
    const first = keep.length ? Math.round(rest / n / 1000) * 1000 : count === 1 ? rest : Math.min(rest, typed ?? defFirst);
    const fresh = buildPayPlan(rest, n, first, now, 0);
    // Đợt sau đợt khoá không lấy ngày hôm nay của đợt 1 mới
    setDraft(renumber([...keep, ...(keep.length ? fresh.map((x) => ({ ...x, due: undefined })) : fresh)]));
  };
  const setStep = (no: number, p: Partial<PayStep>) => setDraft((xs) => xs.map((s) => (s.no === no ? { ...s, ...p } : s)));
  // Đợt 1 = số khách trả hôm nay: chưa lập kế hoạch thì chia lại phần còn lại cho các lần sau
  const setFirst = (amount: number) => setDraft((xs) => {
    if (planned || xs.length < 2) return xs.map((s, i) => (i === 0 ? { ...s, amount } : s));
    const a = Math.min(Math.max(0, amount), target);
    const rest = buildPayPlan(target, xs.length, a, now, 0);
    return rest.map((s, i) => ({ ...s, due: xs[i]?.due ?? s.due }));
  });
  // Xoá một đợt: số tiền của đợt đó dồn vào đợt cuối còn lại để tổng không đổi
  const removeStep = (no: number) => setDraft((xs) => {
    const gone = xs.find((s) => s.no === no);
    const rest = xs.filter((s) => s.no !== no);
    const li = rest.length - 1;
    return renumber(rest.map((s, i) => (i === li && gone && !(planned && locked.includes(s.no)) ? { ...s, amount: s.amount + gone.amount } : s)));
  });
  // Dồn phần lệch vào đợt cuối chưa khoá
  const fixLast = () => setDraft((xs) => {
    const i = [...xs].reverse().findIndex((s) => !(planned && locked.includes(s.no)));
    if (i < 0) return xs;
    const idx = xs.length - 1 - i;
    const diff = target - xs.reduce((s, x) => s + x.amount, 0);
    return xs.map((s, j) => (j === idx ? { ...s, amount: Math.max(0, s.amount + diff) } : s));
  });

  const request = (no: number, amount: number, steps: number) => {
    // Hình thức thanh toán do quầy POS chọn lúc thu
    const d: SiteDeposit = {
      code: nextCode('CO', allCodes(visits), Date.now()), amount, purpose: steps > 1 ? `Thanh toán lần ${no}/${steps}` : 'Thanh toán',
      method: DEP_METHODS[0], status: 'requested', at: Date.now(), by: me.name, quote: q!.code, planNo: no,
    };
    return d;
  };
  const plainText = (xs: PayStep[]) => xs.map((s) => `Lần ${s.no}${s.due ? ` ${dayjs(s.due).format('DD/MM')}` : ''} ${vnd(s.amount)}`).join(' · ');
  const create = () => {
    if (issues.length) { message.warning(issues[0]); return; }
    const plan = renumber(draft);
    const d = request(1, plan[0].amount, plan.length);
    update(v.id, (x) => ({ payPlan: plan, deposits: [d, ...x.deposits] }), {
      text: plan.length > 1
        ? `Lập kế hoạch thanh toán ${plan.length} lần (${plainText(plan)}) · gửi quầy thu lần 1 ${d.code}`
        : `Gửi quầy thu ${d.code} · ${vnd(d.amount)} (trả 1 lần)`,
      by: me.name,
    });
    setEditing(false);
    message.success(`Đã gửi ${d.code} sang quầy thu.`);
  };
  const saveEdit = () => {
    if (issues.length) { message.warning(issues[0]); return; }
    const plan = renumber(draft);
    update(v.id, () => ({ payPlan: plan }), { text: `Sửa kế hoạch thanh toán: ${plan.length} đợt (${plainText(plan)})`, by: me.name });
    setEditing(false);
    message.success('Đã lưu kế hoạch thanh toán.');
  };
  const collect = (r: PayRow) => {
    const d = request(r.no, r.amount - r.paid, v.payPlan!.length);
    update(v.id, (x) => ({ deposits: [d, ...x.deposits] }), { text: `Gửi quầy thu đợt ${r.no} ${d.code} · ${vnd(d.amount)}`, by: me.name });
    message.success(`Đã gửi đợt ${r.no} sang quầy thu.`);
  };
  const act = (d: SiteDeposit, status: 'received' | 'cancelled') => update(v.id, (x) => ({
    deposits: x.deposits.map((y) => (y.code === d.code ? { ...y, status, ...(status === 'received' ? { receivedAt: Date.now() } : { cancelReason: 'Tư vấn cơ sở huỷ yêu cầu' }) } : y)),
  }), { text: `${status === 'received' ? 'Quầy đã thu' : 'Huỷ yêu cầu'} cọc ${d.code} · ${vnd(d.amount)}`, by: status === 'received' ? 'Thu ngân' : me.name });

  const rows: DepRow[] = [
    ...v.deposits.map((d) => ({ key: d.code, code: d.code, where: 'Tư vấn cơ sở', purpose: d.purpose, amount: d.amount, at: d.at, site: d, status: <Tag color={SITE_DEP_ST[d.status].color} className="!m-0">{SITE_DEP_ST[d.status].label}</Tag> })),
    ...(v.onlineDeps ?? []).map((d) => ({ key: d.code, code: d.code, where: 'Telesales (online)', purpose: d.purpose, amount: d.amount, status: <Tag color={d.received ? 'green' : 'gold'} className="!m-0">{d.status}</Tag> })),
  ];
  const columns: ColumnType<DepRow>[] = [
    { title: 'Mã cọc', dataIndex: 'code', render: (x) => <b>{x}</b> },
    { title: 'Nơi tạo', dataIndex: 'where' },
    { title: 'Mục đích', dataIndex: 'purpose' },
    { title: 'Số tiền', dataIndex: 'amount', align: 'right', render: (x) => <b>{vnd(x)}</b> },
    { title: 'Trạng thái', dataIndex: 'status' },
    {
      title: '', key: 'act', width: 170, align: 'right',
      render: (_, r) => r.site?.status === 'requested' && (
        <span className="flex justify-end gap-1">
          <Popconfirm title="Huỷ yêu cầu cọc này?" okText="Huỷ yêu cầu" cancelText="Không" onConfirm={() => act(r.site!, 'cancelled')}><Button size="small" type="text" danger>Huỷ</Button></Popconfirm>
          <Button size="small" onClick={() => act(r.site!, 'received')}>Quầy đã thu</Button>
        </span>
      ),
    },
  ];

  return (
    <>
      <NotStarted v={v} />
      <div className="grid grid-cols-1 items-start gap-3 sm:gap-4 lg:grid-cols-2">
        <Card title="Kế hoạch thanh toán">
          <div className="space-y-3">
            <Row label="Theo phiếu">
              {q ? <span>{q.code}{!quoteValid(q) && <Tag color="orange" className="!ml-2">Chưa có hiệu lực</Tag>}</span>
                : <Link to={`/phieu-bao-gia?v=${v.id}`} className="text-[#3e91ab]">Chưa có phiếu — lập phiếu báo giá</Link>}
            </Row>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-gray-50 p-2.5 dark:bg-gray-700/40"><div className="text-xs text-gray-500">Tổng chi phí</div><div className="mt-0.5 font-bold text-gray-800 dark:text-gray-100">{vnd(total)}</div></div>
              <div className="rounded-lg bg-green-50 p-2.5 dark:bg-green-500/10"><div className="text-xs text-gray-500">Đã thu</div><div className="mt-0.5 font-bold text-green-600">{vnd(paid)}</div>{pending > 0 && <div className="text-[11px] text-amber-600">+ {vnd(pending)} chờ quầy</div>}</div>
              <div className="rounded-lg bg-[#3e91ab]/10 p-2.5"><div className="text-xs text-gray-500">Còn lại</div><div className="mt-0.5 font-bold text-[#3e91ab]">{vnd(Math.max(0, total - paid))}</div></div>
            </div>
            {!editing ? (
              <div className="flex flex-wrap items-center gap-2">
                <Hint>Đã lập kế hoạch thanh toán {v.payPlan!.length} lần — theo dõi và gửi quầy thu ở khung bên phải.</Hint>
                {ok && rowsPlan.some((r) => !locked.includes(r.no)) && (
                  <Button size="small" icon={<Pencil size={13} />} onClick={() => { setDraft(v.payPlan!); setEditing(true); }}>Sửa các đợt chưa thu</Button>
                )}
              </div>
            ) : (
              <>
                <div>
                  <div className="mb-1 text-[13px] font-semibold text-gray-700 dark:text-gray-200">Số lần thanh toán</div>
                  <Select value={draft.length} onChange={pickCount} disabled={!ok} className="w-full"
                    options={Array.from({ length: PLAN_MAX }, (_, i) => ({ value: i + 1, label: `${i + 1} lần` }))} />
                </div>

                <div>
                  <div className="mb-1 text-[13px] font-semibold text-gray-700 dark:text-gray-200">Các lần thanh toán</div>
                  <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-[52px_1fr_150px_32px] gap-3 bg-gray-50 px-3 py-2 text-[11px] font-semibold uppercase text-gray-500 dark:bg-gray-700/40">
                      <span>Lần</span><span className="text-right">Số tiền</span><span>Ngày hẹn (không bắt buộc)</span><span />
                    </div>
                    {draft.map((s, i) => {
                      const lock = planned && locked.includes(s.no);
                      const today = !planned && i === 0;
                      return (
                        <div key={s.no} className="grid grid-cols-[52px_1fr_150px_32px] items-center gap-3 border-t border-gray-100 px-3 py-2 text-[13px] dark:border-gray-700">
                          <span className="font-semibold text-gray-700 dark:text-gray-200">{s.no}{lock && <Tooltip title="Đã thu / đang chờ quầy thu — không sửa"><Lock size={11} className="ml-1 inline text-gray-400" /></Tooltip>}</span>
                          <InputNumber value={s.amount} min={0} step={500_000} disabled={!ok || lock || draft.length === 1} className="!w-full [&_input]:!text-right [&_input]:!font-semibold" formatter={money} parser={unmoney}
                            onChange={(x) => (today ? setFirst(x ?? 0) : setStep(s.no, { amount: x ?? 0 }))} />
                          {today ? <span className="text-xs text-gray-500">Hôm nay</span> : (
                            <DatePicker size="small" value={s.due ? dayjs(s.due) : null} format="DD/MM/YYYY" inputReadOnly placeholder="Chưa hẹn" disabled={!ok || lock}
                              disabledDate={(d) => d.isBefore(dayjs(now), 'day')} onChange={(d) => setStep(s.no, { due: d ? d.startOf('day').valueOf() : undefined })} />
                          )}
                          {draft.length > 1 && !lock && !today
                            ? <Button size="small" type="text" icon={<Trash2 size={14} />} aria-label={`Xoá đợt ${s.no}`} onClick={() => removeStep(s.no)} className="!text-gray-400 hover:!text-red-500" />
                            : <span />}
                        </div>
                      );
                    })}
                    {draftSum !== target && (
                      <div className="flex flex-wrap items-center justify-end gap-1 border-t border-gray-100 px-3 py-1.5 text-xs dark:border-gray-700">
                        Tổng các đợt <b className="text-red-500">{vnd(draftSum)}</b> / {vnd(target)}
                        <Button size="small" type="link" onClick={fixLast} className="!px-1">Dồn phần lệch vào đợt cuối</Button>
                      </div>
                    )}
                  </div>
                  <div className="mt-1 text-[11.5px] text-gray-500">Lần 1 nhập đúng số khách trả hôm nay, phần còn lại tự chia cho các lần sau. Không cần hỏi ngày khách trả — để trống nếu khách chưa nói.</div>
                </div>

                {ok && issues.length > 0 && <div className="text-xs text-red-500">{issues.map((x) => <div key={x}>• {x}</div>)}</div>}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {planned ? (
                    <>
                      <Button type="primary" onClick={saveEdit} disabled={!ok || issues.length > 0}>Lưu kế hoạch</Button>
                      <Button onClick={() => setEditing(false)}>Huỷ</Button>
                    </>
                  ) : (
                    <Button type="primary" onClick={create} disabled={!ok || !room || issues.length > 0}>{draft.length > 1 ? `Lập kế hoạch ${draft.length} lần · gửi quầy thu lần 1` : 'Gửi quầy thu'}</Button>
                  )}
                </div>
              </>
            )}
            <Hint>Chỉ quầy POS thu tiền và chọn hình thức (tiền mặt, thẻ, chuyển khoản); thu ngân vẫn sửa được số tiền nếu khách đổi ý lúc trả. Tư vấn cơ sở không cầm tiền.</Hint>
          </div>
        </Card>

        <Card title="Theo dõi các lần thanh toán" extra={<span className="text-xs text-gray-500">Đã thu <b className="text-green-600">{vnd(paid)}</b> / {vnd(total)}</span>}>
          {total > 0 && (
            <div className="mb-3">
              <div className="h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <div className="h-full rounded-full bg-green-500" style={{ width: `${Math.min(100, (paid / total) * 100)}%` }} />
              </div>
              <div className="mt-1 flex justify-between text-xs text-gray-500"><span>Đã thu {Math.round((paid / total) * 100)}%</span><span>Còn lại <b className="text-gray-700 dark:text-gray-200">{vnd(Math.max(0, total - paid))}</b></span></div>
            </div>
          )}
          {rowsPlan.length ? (
            <div className="divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
              {rowsPlan.map((r) => (
                <div key={r.no} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-[13px]">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-800 dark:text-gray-100">Lần {r.no} · <span className="tabular-nums">{vnd(r.amount)}</span></div>
                    <div className="text-xs text-gray-500">{r.due ? `Hẹn ${dmy(r.due)}` : dmy(r.due)}{r.paid > 0 && r.paid < r.amount ? ` · đã thu ${vnd(r.paid)}` : ''}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Tag color={PAY_STEP_ST[r.state].color} className="!m-0">{stepState(r)}</Tag>
                    {r === nextStep && <Button size="small" onClick={() => collect(r)}>Gửi quầy thu</Button>}
                  </div>
                </div>
              ))}
            </div>
          ) : <Hint>Chưa có kế hoạch thanh toán — lập ở khung bên trái.</Hint>}

          <div className="mb-2 mt-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Các khoản thu · cọc online</div>
          <RTable rowKey="key" columns={columns} dataSource={rows} scrollX={640} empty="Chưa có khoản thu" />
          <Hint>Vòng đời khoản thu dùng chung với Telesales: Chờ quầy thu / Chờ chuyển khoản → Chờ đối soát → Đã nhận → POS trừ vào hoá đơn, hoặc hoàn / khách bỏ cọc. Nút “Quầy đã thu” là minh hoạ cho phần POS báo về.</Hint>
        </Card>
      </div>
    </>
  );
}

export default function DepositAtFacilityPage() {
  const { v } = useVisit();
  return (
    <Shell title="Kế hoạch thanh toán" sub="Tổng chi phí – đã thu – còn lại · số lần thanh toán · quầy POS thu">
      <FlowBar v={v} step="deposit" />
      {v ? <DoctorGate v={v}><Body key={v.id} v={v} /></DoctorGate> : <NoVisit />}
      {/* Ẩn danh sách cọc cả ngày: hiện dưới phiếu của khách đang tư vấn dễ nhầm là cọc của khách này */}
    </Shell>
  );
}
